import React, { useEffect, useCallback } from 'react';
import { Link, useLocation } from 'react-router-dom';

const NAV_LINKS = [
  ['/', 'Home'],
  ['/services', 'Services'],
  ['/about', 'About'],
  ['/our-clients', 'Our Clients'],
  ['/career', 'Career'],
  ['/internship', 'Internship'],
  ['/contact', 'Contact'],
];

export default function Shell({ mobileOpen, onClose }) {
  const location = useLocation();

  // Close on route change
  const handleClose = useCallback(() => {
    onClose?.();
  }, [onClose]);

  useEffect(() => {
    handleClose();
  }, [location.pathname, handleClose]);

  // Lock body scroll when drawer is open
  useEffect(() => {
    document.body.style.overflow = mobileOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [mobileOpen]);

  // Hide the legacy preloader div
  useEffect(() => {
    const preloader = document.querySelector('.preloader');
    if (!preloader) return;
    const timer = setTimeout(() => {
      preloader.style.transition = 'opacity 0.3s ease';
      preloader.style.opacity = '0';
      setTimeout(() => { preloader.style.display = 'none'; }, 300);
    }, 400);
    return () => clearTimeout(timer);
  }, []);

  return (
    <>
      <div className="preloader"><div className="preloader__image"></div></div>
      <div className="custom-cursor__cursor"></div>
      <div className="custom-cursor__cursor-two"></div>

      {/* ── Mobile Nav Drawer (pure React inline styles — no ontech.css dependency) ── */}

      {/* Backdrop overlay */}
      <div
        onClick={onClose}
        style={{
          position: 'fixed',
          inset: 0,
          zIndex: 9998,
          backgroundColor: 'rgba(15,23,42,0.5)',
          opacity: mobileOpen ? 1 : 0,
          visibility: mobileOpen ? 'visible' : 'hidden',
          transition: 'opacity 0.3s ease, visibility 0.3s ease',
          cursor: 'pointer',
        }}
        aria-hidden="true"
      />

      {/* Slide-in panel */}
      <div
        role="dialog"
        id="mobile-nav-drawer"
        aria-modal="true"
        aria-label="Navigation menu"
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          height: '100%',
          width: 'min(86vw, 320px)',
          zIndex: 9999,
          backgroundColor: '#ffffff',
          color: '#0f172a',
          overflowY: 'auto',
          padding: '30px 20px',
          boxShadow: '4px 0 40px rgba(15,23,42,0.18)',
          transform: mobileOpen ? 'translateX(0)' : 'translateX(-100%)',
          transition: 'transform 0.35s cubic-bezier(0.4,0,0.2,1)',
          visibility: mobileOpen ? 'visible' : 'hidden',
        }}
      >
        {/* Close button */}
        <button
          onClick={onClose}
          aria-label="Close menu"
          style={{
            position: 'absolute',
            top: 20,
            right: 16,
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            fontSize: 18,
            color: '#0f172a',
            lineHeight: 1,
            padding: '4px 8px',
          }}
        >
          <i className="fa fa-times" />
        </button>

        {/* Logo */}
        <div style={{ marginBottom: 32 }}>
          <Link to="/" onClick={onClose} aria-label="Home">
            <img src="/assets/images/resources/logo.png" width={143} alt="Magizhini" />
          </Link>
        </div>

        {/* Nav links */}
        <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
          {NAV_LINKS.map(([path, label]) => (
            <li
              key={path}
              style={{
                borderBottom: '1px solid rgba(15,23,42,0.08)',
              }}
            >
              <Link
                to={path}
                onClick={onClose}
                style={{
                  display: 'block',
                  padding: '12px 4px',
                  fontSize: 15,
                  fontWeight: 600,
                  color: location.pathname === path ? '#00A5B5' : '#0f172a',
                  textDecoration: 'none',
                  letterSpacing: '-0.01em',
                  transition: 'color 0.2s',
                }}
              >
                {label}
              </Link>
            </li>
          ))}
        </ul>

        {/* Contact info */}
        <ul style={{ listStyle: 'none', padding: 0, margin: '24px 0 0' }}>
          <li style={{ marginBottom: 10 }}>
            <i className="fa fa-envelope" style={{ marginRight: 8, color: '#00A5B5' }} />
            <a href="mailto:magizhinitech@gmail.com" style={{ color: '#0f172a', textDecoration: 'none', fontSize: 14 }}>
              magizhinitech@gmail.com
            </a>
          </li>
          <li>
            <i className="fa fa-phone-alt" style={{ marginRight: 8, color: '#00A5B5' }} />
            <a href="tel:+16476874226" style={{ color: '#0f172a', textDecoration: 'none', fontSize: 14 }}>
              +1(647)-687-4226
            </a>
          </li>
        </ul>

        {/* Social links */}
        <div style={{ marginTop: 24, display: 'flex', gap: 16 }}>
          <a href="https://facebook.com/magizhinitech" className="fab fa-facebook-square" aria-label="Facebook"
            style={{ fontSize: 20, color: '#0f172a', textDecoration: 'none' }} />
          <a href="https://www.instagram.com/magizhinitech" className="fab fa-instagram" aria-label="Instagram"
            style={{ fontSize: 20, color: '#0f172a', textDecoration: 'none' }} />
          <a href="https://linkedin.com/company/magizhinitech" className="fab fa-linkedin" aria-label="LinkedIn"
            style={{ fontSize: 20, color: '#0f172a', textDecoration: 'none' }} />
        </div>
      </div>

      {/* Search popup (unchanged) */}
      <div className="search-popup">
        <div className="search-popup__overlay search-toggler"></div>
        <div className="search-popup__content">
          <form action="#" onSubmit={(e) => e.preventDefault()}>
            <label htmlFor="search" className="sr-only">search here</label>
            <input type="text" id="search" placeholder="Search Here..." />
            <button type="submit" aria-label="search submit" className="thm-btn">
              <i className="icon-search"></i>
            </button>
          </form>
        </div>
      </div>

      <a href="#" data-target="html" className="scroll-to-target scroll-to-top">
        <i className="fa fa-angle-up"></i>
      </a>
    </>
  );
}
